import axios from "../axios-base";

// Cache for delivery info - 60 second TTL
let deliveryInfoCache = {
  data: null,
  timestamp: 0,
  TTL: 60 * 1000, // 60 seconds
};

/**
 * Get delivery configuration with caching
 *
 * Delivery types:
 * - fixed-fee: Fixed delivery fee regardless of order total
 * - no-delivery: No delivery service
 * - free-over-threshold: Free delivery when order meets threshold
 * - external-fee: External delivery fee
 * - all-free: Free delivery always
 *
 * @returns {Promise<Object>} Delivery configuration object
 */
export const getDeliveryInfo = async () => {
  try {
    const now = Date.now();

    // Return cached data if still valid
    if (
      deliveryInfoCache.data &&
      now - deliveryInfoCache.timestamp < deliveryInfoCache.TTL
    ) {
      return deliveryInfoCache.data;
    }

    // Fetch fresh data
    const response = await axios.get("/gerelt/orders/delivery/info");
    const data = response?.data?.data || {
      type: "fixed-fee",
      deliveryFee: 0,
      minFreeOrderTotal: 0,
      label: "Хүргэлттэй (Үнэ тогтмол)",
    };

    // Update cache
    deliveryInfoCache = {
      data,
      timestamp: now,
      TTL: deliveryInfoCache.TTL,
    };

    return data;
  } catch (error) {
    console.error("Error fetching delivery info:", error);
    // Return default delivery info on error
    return {
      type: "fixed-fee",
      deliveryFee: 0,
      minFreeOrderTotal: 0,
      label: "Хүргэлттэй (Үнэ тогтмол)",
    };
  }
};

/**
 * Calculate delivery fee amount from delivery configuration and subtotal.
 * Mirrors backend rules from service/modules/gerelt-custom/utils/deliveryCalculator.js.
 *
 * @param {Object} deliveryInfo
 * @param {Number} subtotal
 * @returns {Number}
 */
export const calculateDeliveryFeeAmount = (deliveryInfo, subtotal = 0) => {
  if (!deliveryInfo) return 0;

  const type = String(deliveryInfo?.type || "fixed-fee").trim();
  const fee = Number(deliveryInfo?.deliveryFee || 0);
  const threshold = Number(deliveryInfo?.minFreeOrderTotal || 0);
  const safeSubtotal = Number.isFinite(subtotal) ? subtotal : 0;

  switch (type) {
    case "no-delivery":
    case "all-free":
      return 0;

    case "fixed-fee":
      return Number.isFinite(fee) && fee > 0 ? fee : 0;

    case "external-fee":
      // Admin will set the fee externally after order; do not charge at checkout
      return 0;

    case "free-over-threshold":
      if (Number.isFinite(threshold) && safeSubtotal >= threshold) {
        return 0;
      }
      return Number.isFinite(fee) && fee > 0 ? fee : 0;

    default:
      return Number.isFinite(fee) && fee > 0 ? fee : 0;
  }
};

/**
 * Delivery row value for checkout summary.
 * Returns only the value part (e.g. "Үнэгүй", "Хүргэлтгүй", "4,000 ₮").
 */
export const getDeliveryFeeDisplayValue = (deliveryInfo, subtotal = 0) => {
  if (!deliveryInfo) return "Үнэгүй";

  const type = String(deliveryInfo?.type || "fixed-fee").trim();
  const fee = calculateDeliveryFeeAmount(deliveryInfo, subtotal);

  if (type === "no-delivery") return "Хүргэлтгүй";
  return fee > 0 ? `${fee.toLocaleString("mn-MN")} ₮` : "Үнэгүй";
};

/**
 * Get delivery fee formatted for display
 *
 * @param {Object} deliveryInfo - Delivery configuration
 * @param {Number} subtotal - Order subtotal
 * @returns {String} Formatted delivery fee text
 */
export const getDeliveryFeeText = (deliveryInfo, subtotal = 0) => {
  if (!deliveryInfo) return "Хүргэлт: -";

  const value = getDeliveryFeeDisplayValue(deliveryInfo, subtotal);
  if (value === "Хүргэлтгүй") return value;
  if (value === "Үнэгүй") return "Хүргэлт үнэгүй";
  return `Хүргэлт: ${value}`;
};

/**
 * Get delivery info description (longer format for UI display)
 *
 * @param {Object} deliveryInfo - Delivery configuration
 * @returns {String} Description text
 */
export const getDeliveryDescription = (deliveryInfo) => {
  if (!deliveryInfo) return "";

  const { type, deliveryFee, minFreeOrderTotal, label } = deliveryInfo;

  switch (type) {
    case "no-delivery":
      return "Хүргэлтийн сервис идэвхгүй";

    case "all-free":
      return "Бүх хүргэлт үнэгүй";

    case "fixed-fee":
      return deliveryFee > 0
        ? `${deliveryFee.toLocaleString("mn-MN")} ₮ хүргэлтийн үнэ (захиалгын хэмжээ ба хамаагүй)`
        : "Хүргэлт үнэгүй";

    case "external-fee":
      return "Хүргэлтийн үнийг дараа админ тогтооно";

    case "free-over-threshold":
      return `${minFreeOrderTotal.toLocaleString("mn-MN")} ₮-ын дээш захиалга үнэгүй хүргэнэ. ${deliveryFee > 0 ? `Үгүй бол ${deliveryFee.toLocaleString("mn-MN")} ₮` : ""}`;

    default:
      return label || "";
  }
};

// Export cache clear function for testing
export const clearDeliveryInfoCache = () => {
  deliveryInfoCache = {
    data: null,
    timestamp: 0,
    TTL: 60 * 1000,
  };
};
